import { cn } from '@ifixit/ui/lib/utils';
import { useBreakpointValue } from '@ifixit/ui/hooks/use-media-query';
import { useRef, useEffect, useState, ReactNode } from 'react';
import type { CSSProperties, Ref } from 'react';
import { easeInOutQuad } from './easing';

type BeforeAfterOpacityState = {
   after: CSSProperties;
   before: CSSProperties;
};

const gradientWidths = { base: 40, sm: 50, md: 70, lg: 100 };

export function FlexScrollGradient({
   ref,
   className,
   children,
}: {
   ref?: Ref<HTMLDivElement>;
   className?: string;
   children?: ReactNode;
}) {
   const internalRef = useRef<HTMLDivElement>(null);

   const [flexScrollState, setFlexScrollState] = useState<BeforeAfterOpacityState>({
      before: { opacity: 0 },
      after: { opacity: 0 },
   });

   const gradientWidthPX = useBreakpointValue(gradientWidths) ?? gradientWidths.base;
   const doMeasure = (el: HTMLElement) => {
      const { scrollWidth, clientWidth, scrollLeft, clientHeight } = el;

      const isScrollable = scrollWidth > clientWidth;
      if (!isScrollable) {
         setFlexScrollState(() => ({
            before: { opacity: 0, height: 0 },
            after: { opacity: 0, height: 0 },
         }));
         return;
      }

      const scrollPosition = scrollLeft + clientWidth;
      const afterStart = scrollWidth - gradientWidthPX;
      const height = `${clientHeight}px`;

      const beforeGradientShownPercent =
         scrollLeft < gradientWidthPX ? scrollLeft / gradientWidthPX : 1;

      const afterGradientShownPercent =
         scrollPosition >= afterStart ? 1 - (scrollPosition - afterStart) / gradientWidthPX : 1;

      const beforeGradientOpacity = easeInOutQuad(Math.max(0, beforeGradientShownPercent));
      const afterGradientOpacity = easeInOutQuad(Math.max(0, afterGradientShownPercent));

      setFlexScrollState(() => ({
         before: { opacity: beforeGradientOpacity, height },
         after: { opacity: afterGradientOpacity, height },
      }));
   };

   useEffect(() => {
      if (!internalRef.current) {
         return;
      }

      const element = internalRef.current;

      const measure = () => {
         doMeasure(element);
      };

      // Use ResizeObserver for size changes instead of reading on every render
      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(element);

      element.addEventListener('scroll', measure);

      return () => {
         resizeObserver.disconnect();
         element.removeEventListener('scroll', measure);
      };
   }, [gradientWidthPX]);

   const sharedGradientStyle: CSSProperties = {
      width: `${gradientWidthPX}px`,
   };

   return (
      <div className={cn('flex relative isolate', className)}>
         <div
            aria-hidden
            className="absolute left-[-1px] pointer-events-none z-[9999] bg-[linear-gradient(270deg,rgba(249,250,251,0)_0%,#F9FAFB_106.41%)]"
            style={{ ...sharedGradientStyle, ...flexScrollState.before }}
         />
         <div
            aria-hidden
            className="absolute right-[-1px] pointer-events-none z-[9999] bg-[linear-gradient(90deg,rgba(249,250,251,0)_0%,#F9FAFB_106.41%)]"
            style={{ ...sharedGradientStyle, ...flexScrollState.after }}
         />
         <div
            ref={element => {
               internalRef.current = element;
               if (typeof ref === 'function') {
                  ref(element);
               } else if (ref) {
                  ref.current = element;
               }
            }}
            className="flex overflow-auto"
         >
            {children}
         </div>
      </div>
   );
}
